// ============================================================================
// TASKSTORE.JS - VERSIÓN COMPLETA SINCRONIZADA CON AUTHCONTEXT
// ============================================================================
// Esta versión incluye TODAS las funciones del TaskStore original pero
// sincronizada con AuthContext eliminando la duplicación de estado
// ============================================================================

import { create } from 'zustand';
import { registerTaskStoreNotifier } from '../auth/AuthContext';
import apiClient from '../services/apiClient';

// Importar servicios necesarios
import documentationService from '../services/documentationService';
import documentTaskService from '../services/DocumentTaskService';

// ============================================================================
// ACCESO A API
// ============================================================================
// La capa de red vive en el apiClient único (base URL por tenant derivada en
// cada llamada, token Bearer e interceptor de sesión caducada). Antes había aquí
// un CLON del cliente (resolveToken + headers Bearer + interceptor 401/403); se
// ha eliminado. El 401/403 con token lo gestiona ahora el apiClient de forma
// global (vía el handler que registra AuthContext), así que este store ya no
// duplica ese manejo.
//
// Opciones de token para apiClient. Replica la semántica de resolveToken: si el
// llamador pasa un token explícito se usa; si no (arranque, authToken null) se
// deja `undefined` para que el apiClient tome el de localStorage por defecto.
// Así se evitan peticiones sin Authorization durante la carrera de arranque.
const authOpts = (authToken) => ({ token: authToken || undefined });

// ============================================================================
// FUNCIONES DE UTILIDAD
// ============================================================================
const sanitizeHtmlForAPI = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/&nbsp;/g, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<div>/gi, '\n')
    .replace(/<\/div>/gi, '')
    .replace(/\n\s*\n/g, '\n')
    .trim();
};

const convertSprintToAPI = (sprintValue) => {
  if (sprintValue === '' || sprintValue === 'null' || sprintValue === null) return null;
  if (sprintValue === 'sin-asignar') return null;
  return sprintValue;
};

// El apiClient devuelve el ENVELOPE JSON completo ({status, message, data,
// timestamp}) y LANZA (HttpError) en respuestas no-ok. Esta función normaliza
// ese envelope al shape { success, data, message/error } que consume el store.
// Antes recibía { data: envelope, status: httpStatus } del clon; ahora recibe el
// envelope directo (el status HTTP ya lo cubre el throw del apiClient, por eso
// el tercer caso —cuerpo 2xx sin envelope estándar— ya no comprueba el status).
const extractResponseData = (envelope, context = 'operation') => {
  console.log(`Extracting data for ${context}:`, envelope);

  if (envelope && envelope.status === 'success') {
    return { success: true, data: envelope.data, message: envelope.message };
  }

  if (envelope && envelope.status === 'error') {
    return { success: false, error: envelope.message, data: null };
  }

  if (envelope) {
    return { success: true, data: envelope, message: 'Operación exitosa' };
  }

  return { success: false, error: 'Formato de respuesta inesperado', data: null };
};

// ============================================================================
// ESTADO INICIAL COMPLETO
// ============================================================================
const initialState = {
  // Datos principales
  tasks: [],
  currentTask: null,
  sprints: [],
  activeSprint: null,
  // Sprint en estado 'planificacion' (se llena de tareas mientras el activo
  // sigue en curso). Invariante por tenant: máx 1 activo + máx 1 planificación.
  planningSprint: null,
  documents: [],
  epics: [], // 🆕 ÉPICAS CENTRALIZADAS

  // Filtros y búsqueda
  filters: {
    estado: 'todos',
    prioridad: 'todas',
    sprint: 'todos',
    epica: 'todas',
    incluirCerradas: false,
  },
  searchTerm: '',
  sprintStatusFilter: [], // Filtro de estado desde hero cards ([] = todos)

  // UI Estado
  // isLoading = "hay una mutación/fetch en curso" (lo leen estados disabled de
  //   varios componentes: TaskDetail, TaskStatusControl, SprintManager...).
  // isInitializing = SOLO la carga inicial (initialize). El overlay global de
  //   pantalla completa se ata a ESTE flag, no a isLoading, para que las
  //   micro-acciones (cambiar estado, mover, asignar...) no lo hagan parpadear.
  isLoading: false,
  isInitializing: false,
  viewMode: 'table',
  error: null,

  // Stats y métricas
  dashboardStats: {
    totalTasks: 0,
    pendingTasks: 0,
    inProgressTasks: 0,
    completedTasks: 0,
    seoValidatedTasks: 0,
    activeSprint: null,
  },

  // Timestamps
  lastUpdate: new Date().toISOString(),
  lastSync: null,

  // Estado de conexión
  connectionStatus: 'disconnected',
  apiHealth: null,

  // Estado de sincronización con AuthContext
  authSyncStatus: 'disconnected'
};

// ============================================================================
// STORE PRINCIPAL COMPLETO Y SINCRONIZADO
// ============================================================================
export const useTaskStore = create((set, get) => {
  
  // ============================================================================
  // SISTEMA DE SINCRONIZACIÓN CON AUTHCONTEXT
  // ============================================================================
  const handleAuthNotification = (type, data) => {
    console.log(`📨 TaskStore recibió notificación: ${type}`, data);
    
    switch (type) {
      case 'LOGIN_SUCCESS':
        console.log('🔐 Sincronizando con login exitoso');
        set({ 
          authSyncStatus: 'connected',
          error: null 
        });
        
        // Reinicializar datos con el usuario autenticado
        get().initialize(data.user, data.token);
        break;
        
      case 'LOGIN_FAILURE':
        console.log('❌ Login falló, limpiando TaskStore');
        set({ 
          ...initialState,
          authSyncStatus: 'error',
          error: data.error || 'Error de autenticación'
        });
        break;
        
      case 'LOGOUT':
        console.log('🚪 Logout detectado, limpiando TaskStore');
        set({ 
          ...initialState,
          authSyncStatus: 'disconnected'
        });
        break;
        
      case 'USER_UPDATE':
        console.log('👤 Usuario actualizado');
        set({ lastUpdate: new Date().toISOString() });
        break;
        
      default:
        console.log(`Notificación no manejada: ${type}`);
    }
  };

  // Registrar con AuthContext al crear el store
  registerTaskStoreNotifier(handleAuthNotification);
  console.log('🔗 TaskStore registrado con AuthContext');

  return {
    ...initialState,

    // ============================================================================
    // INICIALIZACIÓN SINCRONIZADA
    // ============================================================================
    initialize: async (user = null, token = null) => {
      console.log('🚀 Inicializando TaskStore sincronizado...', { 
        user: user?.nombre, 
        hasToken: !!token 
      });
      
      set({
        isLoading: true,
        isInitializing: true,
        error: null,
        connectionStatus: 'connecting',
        authSyncStatus: user ? 'connected' : 'disconnected'
      });
      
      try {
        // Solo proceder si tenemos datos de usuario
        if (!user || !token) {
          console.log('⚠️ No hay datos de usuario, esperando autenticación');
          set({
            isLoading: false,
            isInitializing: false,
            connectionStatus: 'waiting',
            authSyncStatus: 'disconnected'
          });
          return;
        }
        
        console.log('✅ Usuario disponible, cargando datos...');
        
        // Health check de API EN PARALELO con la primera carga (revisión 2026-10,
        // informe 05 MEDIA-4 paso 2): antes se esperaba health (~110 ms) antes de
        // pedir nada. Su resultado se aplica en cuanto llega, igual que antes
        // (apiHealth + connectionStatus 'connected' si success). Si la petición
        // de health FALLA, el arranque termina en el catch de abajo con el mismo
        // estado de error que antes, pero tras esperar a la carga en curso.
        const healthPromise = apiClient.get('health', null, authOpts(token)).then((healthResponse) => {
          const healthResult = extractResponseData(healthResponse, 'health check');
          if (healthResult.success) {
            console.log('💚 API Health Check OK');
            set({ apiHealth: healthResult.data, connectionStatus: 'connected' });
          }
          return healthResult;
        });

        // Cargar datos iniciales
        console.log('📥 Cargando datos iniciales...');
        const dataPromise = Promise.allSettled([
          get().fetchTasks(token, user),
          get().fetchSprints(token, user),
          get().fetchDashboardStats(token, user),
          get().fetchDocuments(token, user),
          get().fetchEpics(token) // 🆕 CARGAR ÉPICAS
        ]);

        const [healthOutcome] = await Promise.allSettled([healthPromise, dataPromise]);
        if (healthOutcome.status === 'rejected') {
          throw healthOutcome.reason;
        }
        
        set({
          isLoading: false,
          isInitializing: false,
          lastSync: new Date().toISOString(),
          authSyncStatus: 'connected'
        });

        console.log('✅ TaskStore inicializado correctamente');
        
      } catch (error) {
        console.error('❌ Error inicializando TaskStore:', error);
        set({
          error: 'Error al conectar con la API: ' + error.message,
          isLoading: false,
          isInitializing: false,
          connectionStatus: 'error',
          authSyncStatus: 'error'
        });
      }
    },

    // ============================================================================
    // GESTIÓN DE TAREAS COMPLETA
    // ============================================================================
    fetchTasks: async (authToken = null, user = null, filters = {}) => {
      try {
        console.log('📋 Obteniendo tareas con auth externa');
        set({ isLoading: true, error: null });
        
        const params = {};

        // Filtrar por usuario si no es admin
        if (user && user.rol !== 'admin') {
          params.user_id = user.id;
        }

        const response = await apiClient.get('tasks', params, authOpts(authToken));
        const result = extractResponseData(response, 'tasks');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al obtener tareas');
        }
        
        const apiTasks = Array.isArray(result.data) ? result.data : [];
        console.log(`📋 ${apiTasks.length} tareas obtenidas`);
        
        set({ 
          tasks: apiTasks,
          isLoading: false,
          lastUpdate: new Date().toISOString()
        });
        
        return { success: true, data: apiTasks };
        
      } catch (error) {
        console.error('❌ Error obteniendo tareas:', error);
        set({ 
          error: 'Error al obtener tareas: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    fetchTask: async (taskId, authToken = null) => {
      try {
        console.log('📋 Obteniendo tarea específica:', taskId);
        const response = await apiClient.get('task', { id: taskId }, authOpts(authToken));
        const result = extractResponseData(response, 'single task');
        
        if (!result.success) {
          throw new Error(result.error || 'Tarea no encontrada');
        }
        
        set({ currentTask: result.data });
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error obteniendo tarea:', error);
        return { success: false, error: error.message };
      }
    },

    createTask: async (taskData, authToken = null, user = null) => {
      if (!authToken || !user) {
        console.error('❌ createTask requiere autenticación');
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('📝 Creando tarea con auth externa');
        set({ isLoading: true, error: null });
        
        const apiData = {
          nombre: taskData.nombre,
          descripcion: sanitizeHtmlForAPI(taskData.descripcion) || '',
          prioridad: taskData.prioridad || 'media',
          epica: taskData.epica || '',
          estado: taskData.estado || 'pendiente',
          afectacion: sanitizeHtmlForAPI(taskData.afectacion) || '',
          etiquetas: taskData.etiquetas || [],
          validadoPorSEO: Boolean(taskData.validadoPorSEO ?? false),
          responsable: taskData.responsableId || taskData.responsable || null,
          sprint_actual: convertSprintToAPI(taskData.sprintActual),
          fecha_vencimiento: taskData.fecha_vencimiento || null,
          veces_completada: 0,
          created_by: user.id,
          orden_global: 0,
          orden_sprint: 0
        };
        
        console.log('Creando tarea con usuario real:', user.id);
        
        const response = await apiClient.post('tasks', apiData, authOpts(authToken));
        const result = extractResponseData(response, 'task creation');
        
        if (!result.success) {
          const errorMsg = result.error || 'Error al crear tarea';
          throw new Error(errorMsg);
        }
        
        // Perf: insertar la tarea devuelta en vez de refetch de la lista entera.
        // Normalizada al shape del listado (getBasicTask no trae los alias
        // camelCase y sin sprintActual la tarea caería al backlog en el panel).
        // Fallback a refetch solo si el backend no devolviera la tarea completa.
        if (result.data && result.data.id) {
          const nueva = {
            ...result.data,
            sprintActual: result.data.sprintActual ?? result.data.sprint_actual ?? null,
            vecesRealizada: result.data.vecesRealizada ?? result.data.veces_completada ?? 0,
            total_comentarios: result.data.total_comentarios ?? 0,
            total_archivos: result.data.total_archivos ?? 0,
            orden: result.data.orden ?? result.data.orden_global ?? 0,
          };
          set(state => ({ tasks: [...state.tasks, nueva], isLoading: false }));
        } else {
          await get().fetchTasks(authToken, user);
          set({ isLoading: false });
        }
        console.log('✅ Tarea creada exitosamente');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error creando tarea:', error);
        set({ 
          error: 'Error al crear tarea: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    updateTask: async (taskId, updates, authToken = null) => {
      if (!authToken) {
        console.error('❌ updateTask requiere autenticación');
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      // Snapshot para rollback si la red falla (UI optimista).
      const prevTask = get().tasks.find(t => t.id === taskId);
      const prevCurrentTask = get().currentTask;

      // Aplicar el cambio en local YA (optimista) + marcar carga en curso.
      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === taskId ? { ...task, ...updates } : task
        ),
        currentTask: state.currentTask && state.currentTask.id === taskId
          ? { ...state.currentTask, ...updates }
          : state.currentTask,
        // Nota perf: sin isLoading global en mutaciones optimistas — el doble
        // toggle re-renderizaba toda la app (Layout suscribe isLoading).
        error: null
      }));

      try {
        console.log('✏️ Actualizando tarea:', taskId, updates);

        let apiData = {};
        
        if (updates.nombre !== undefined) apiData.nombre = updates.nombre;
        if (updates.descripcion !== undefined) apiData.descripcion = sanitizeHtmlForAPI(updates.descripcion);
        if (updates.estado !== undefined) apiData.estado = updates.estado;
        if (updates.prioridad !== undefined) apiData.prioridad = updates.prioridad;
        if (updates.epica !== undefined) apiData.epica = updates.epica;
        
        if (updates.sprintActual !== undefined) {
          const convertedValue = convertSprintToAPI(updates.sprintActual);
          apiData.sprint_actual = convertedValue;
        }
        
        if (updates.afectacion !== undefined) apiData.afectacion = sanitizeHtmlForAPI(updates.afectacion);
        if (updates.etiquetas !== undefined) apiData.etiquetas = updates.etiquetas;
        if (updates.orden !== undefined) apiData.orden = updates.orden;

        // ✅ Agregar soporte para orden_sprint y orden_global
        if (updates.orden_sprint !== undefined) {
          apiData.orden_sprint = updates.orden_sprint;
          console.log('📦 TaskStore: Agregando orden_sprint al payload:', updates.orden_sprint);
        }
        if (updates.orden_global !== undefined) {
          apiData.orden_global = updates.orden_global;
          console.log('📦 TaskStore: Agregando orden_global al payload:', updates.orden_global);
        }

        if (updates.validadoPorSEO !== undefined) {
          apiData.validadoPorSEO = updates.validadoPorSEO ? 1 : 0;
        }

        // Marca de bloqueo (a la espera). Se envía como 1/0 al backend.
        if (updates.bloqueada !== undefined) {
          apiData.bloqueada = updates.bloqueada ? 1 : 0;
        }

        // Fecha de vencimiento (opcional, nullable). '' → null para desasignar
        // la fecha; el backend valida el formato ISO y descarta basura.
        if (updates.fecha_vencimiento !== undefined) {
          apiData.fecha_vencimiento = updates.fecha_vencimiento || null;
        }

        if (updates.responsableId !== undefined) {
          apiData.responsable = updates.responsableId || null;
        } else if (updates.responsable !== undefined) {
          apiData.responsable = updates.responsable || null;
        }

        // Persona asignada. Puede ser null (desasignar) → se envía igualmente.
        if (updates.assigned_to !== undefined) {
          apiData.assigned_to = updates.assigned_to;
        }

        console.log('🚀 TaskStore: Enviando al backend:', { taskId, apiData });
        const response = await apiClient.post(`update_task&id=${taskId}`, apiData, authOpts(authToken));
        const result = extractResponseData(response, 'task update');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al actualizar tarea');
        }
        
        console.log('✅ Tarea actualizada exitosamente');
        return { success: true, data: result.data };

      } catch (error) {
        console.error('❌ Error actualizando tarea:', error);
        // Rollback: revertir el cambio optimista al estado previo.
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === taskId ? (prevTask || task) : task
          ),
          currentTask: prevCurrentTask && prevCurrentTask.id === taskId
            ? prevCurrentTask
            : state.currentTask,
          error: 'Error al actualizar tarea: ' + error.message
        }));
        return { success: false, error: error.message };
      }
    },

    deleteTask: async (taskId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('🗑️ Eliminando tarea:', taskId);
        set({ isLoading: true, error: null });
        
        const response = await apiClient.post(`delete_task&id=${taskId}`, {}, authOpts(authToken));
        const result = extractResponseData(response, 'task deletion');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al eliminar tarea');
        }
        
        // Actualizar estado local
        set(state => ({
          tasks: state.tasks.filter(task => task.id !== taskId),
          currentTask: state.currentTask && state.currentTask.id === taskId ? null : state.currentTask,
          isLoading: false
        }));
        
        console.log('✅ Tarea eliminada exitosamente');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error eliminando tarea:', error);
        set({ 
          error: 'Error al eliminar tarea: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    // ============================================================================
    // SOPORTE DEFERRED-DELETE (borrado con deshacer)
    // ============================================================================
    // Quita la tarea SOLO del estado local (sin backend). La usa el patrón
    // deferred-delete: la fila desaparece al instante y el borrado real se
    // confirma después si no se deshace.
    removeTaskLocal: (taskId) => set(state => ({
      tasks: state.tasks.filter(task => task.id !== taskId),
      currentTask: state.currentTask && state.currentTask.id === taskId
        ? null
        : state.currentTask,
    })),

    // Reinserta una tarea en el estado local (deshacer un borrado diferido).
    // Idempotente: si ya está, no duplica. El orden final lo resuelve el sort
    // por orden_sprint/orden_global de las vistas.
    restoreTaskLocal: (task) => set(state => (
      state.tasks.some(t => t.id === task.id)
        ? {}
        : { tasks: [...state.tasks, task] }
    )),

    // Elimina SOLO en el backend (la fila ya se quitó en local de forma
    // optimista). No toca isLoading ni el estado local; el llamador decide qué
    // hacer si falla (típicamente restoreTaskLocal + toast de error).
    commitDeleteTask: async (taskId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      try {
        const response = await apiClient.post(`delete_task&id=${taskId}`, {}, authOpts(authToken));
        const result = extractResponseData(response, 'task deletion (deferred)');
        if (!result.success) {
          throw new Error(result.error || 'Error al eliminar tarea');
        }
        return { success: true, data: result.data };
      } catch (error) {
        console.error('❌ Error confirmando borrado diferido:', error);
        return { success: false, error: error.message };
      }
    },

// Crear borrador de tarea (sin datos, solo ID)
createDraftTask: async (authToken = null) => {
  if (!authToken) {
    console.error('❌ createDraftTask requiere autenticación');
    return { success: false, error: 'Usuario no autenticado' };
  }
  
  try {
    console.log('📝 Creando borrador de tarea...');
    
    const response = await apiClient.post('create_draft_task', {}, authOpts(authToken));
    const result = extractResponseData(response, 'draft task creation');
    
    if (!result.success) {
      throw new Error(result.error || 'Error al crear borrador');
    }
    
    console.log('✅ Borrador creado con ID:', result.data.id);
    return { success: true, data: result.data };
    
  } catch (error) {
    console.error('❌ Error creando borrador:', error);
    return { success: false, error: error.message };
  }
},

// Guardar borrador de tarea (actualizar con datos completos)
saveDraftTask: async (taskId, taskData, authToken = null) => {
  if (!authToken) {
    console.error('❌ saveDraftTask requiere autenticación');
    return { success: false, error: 'Usuario no autenticado' };
  }
  
  try {
    console.log('💾 Guardando borrador de tarea:', taskId);
    console.log('📊 TaskData recibido:', taskData);
    console.log('🎯 Sprint en taskData:', taskData.sprintActual);
    set({ isLoading: true, error: null });

    const apiData = {
      nombre: taskData.nombre,
      descripcion: sanitizeHtmlForAPI(taskData.descripcion) || '',
      prioridad: taskData.prioridad || 'media',
      epica: taskData.epica || '',
      estado: taskData.estado || 'pendiente',
      afectacion: sanitizeHtmlForAPI(taskData.afectacion) || '',
      etiquetas: taskData.etiquetas || [],
      validadoPorSEO: Boolean(taskData.validadoPorSEO ?? false),
      responsable: taskData.responsable || '',
      // 🔧 SIEMPRE enviar sprint_actual, incluso si es null (para Backlog)
      sprint_actual: taskData.sprintActual !== undefined ? taskData.sprintActual : null,
      // Fecha de vencimiento (opcional, nullable). '' → null para no fijar fecha.
      fecha_vencimiento: taskData.fecha_vencimiento || null
    };

    console.log('📤 ApiData a enviar al backend:', apiData);
    console.log('🎯 sprint_actual en apiData:', {
      value: apiData.sprint_actual,
      type: typeof apiData.sprint_actual,
      isNull: apiData.sprint_actual === null,
      original: taskData.sprintActual
    });
    
    // Solo añadir assigned_to si tiene valor
    if (taskData.assigned_to) {
      apiData.assigned_to = taskData.assigned_to;
    }

    console.log('🚀 FINAL - Datos que se envían al backend:', JSON.stringify(apiData, null, 2));
    console.log('🚀 FINAL - sprint_actual específicamente:', apiData.sprint_actual);

    const response = await apiClient.post(`save_draft_task&id=${taskId}`, apiData, authOpts(authToken));
    const result = extractResponseData(response, 'draft task save');
    
    if (!result.success) {
      throw new Error(result.error || 'Error al guardar borrador');
    }
    
    console.log('✅ Borrador guardado exitosamente');
    
    // Añadir la tarea al store
    set(state => ({
      tasks: [...state.tasks, result.data],
      isLoading: false
    }));
    
    return { success: true, data: result.data };
    
  } catch (error) {
    console.error('❌ Error guardando borrador:', error);
    set({ 
      error: 'Error al guardar tarea: ' + error.message,
      isLoading: false 
    });
    return { success: false, error: error.message };
  }
},
    moveTaskToSprint: async (taskId, sprintId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }

      // Snapshot para rollback (UI optimista).
      const prevTask = get().tasks.find(t => t.id === taskId);
      const prevCurrentTask = get().currentTask;

      // Mover en local YA (optimista) + marcar carga en curso.
      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === taskId ? { ...task, sprintActual: sprintId } : task
        ),
        currentTask: state.currentTask && state.currentTask.id === taskId
          ? { ...state.currentTask, sprintActual: sprintId }
          : state.currentTask,
        error: null
      }));

      try {
        console.log('🔄 Moviendo tarea a sprint:', { taskId, sprintId });

        const updates = { sprint_actual: sprintId };
        const response = await apiClient.post(`update_task&id=${taskId}`, updates, authOpts(authToken));
        const result = extractResponseData(response, 'move task');

        if (!result.success) {
          throw new Error(result.error || 'Error al mover tarea');
        }

        // Fusionar la tarea que devuelve el backend: un rescate desde un sprint
        // archivado puede venir reabierta (estado/validadoPorSEO cambiados).
        if (result.data && result.data.id) {
          set(state => ({
            tasks: state.tasks.map(task =>
              task.id === taskId ? { ...task, ...result.data, sprintActual: sprintId } : task
            ),
            currentTask: state.currentTask && state.currentTask.id === taskId
              ? { ...state.currentTask, ...result.data, sprintActual: sprintId }
              : state.currentTask
          }));
        }

        const destination = sprintId ? `sprint ${sprintId}` : 'backlog';
        console.log(`✅ Tarea movida a ${destination} exitosamente`);

        return {
          success: true,
          data: result.data,
          message: `Tarea movida a ${destination === 'backlog' ? 'backlog' : 'sprint'} exitosamente`
        };

      } catch (error) {
        console.error('❌ Error moviendo tarea:', error);
        // Rollback al contenedor previo.
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === taskId ? (prevTask || task) : task
          ),
          currentTask: prevCurrentTask && prevCurrentTask.id === taskId
            ? prevCurrentTask
            : state.currentTask,
          error: 'Error al mover la tarea: ' + error.message
        }));
        return { success: false, error: error.message };
      }
    },

    // "Enviar a": mueve la tarea al contenedor destino (sprint activo o backlog)
    // y la coloca al inicio/final. El backend calcula el orden extremo y devuelve
    // la tarea ya actualizada (sprint_actual + ordenes); aquí solo reflejamos ese
    // resultado en el estado local para reordenar sin refetch.
    sendTaskToPosition: async (taskId, target, position, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }

      // Snapshot para rollback (UI optimista).
      const prevTask = get().tasks.find(t => t.id === taskId);
      // Contenedor destino que SÍ conocemos ya (el orden fino lo calcula el
      // backend y llega en la respuesta; hasta entonces mantenemos el orden actual).
      const optimisticSprint = target === 'backlog' ? null : (get().activeSprint?.id ?? null);

      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === taskId
            ? { ...task, sprint_actual: optimisticSprint, sprintActual: optimisticSprint }
            : task
        ),
        error: null,
      }));

      try {
        console.log('📍 Enviando tarea a posición:', { taskId, target, position });

        const response = await apiClient.post(
          `send_task_to&id=${taskId}`,
          { target, position },
          authOpts(authToken)
        );
        const result = extractResponseData(response, 'send task to position');

        if (!result.success) {
          throw new Error(result.error || 'Error al mover la tarea');
        }

        const updated = result.data || {};
        const nextSprint = updated.sprint_actual ?? null;

        // Reconciliar con el resultado autoritativo del backend (sprint + órdenes).
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === taskId
              ? {
                  ...task,
                  sprint_actual: nextSprint,
                  sprintActual: nextSprint,
                  orden_sprint: updated.orden_sprint ?? task.orden_sprint,
                  orden_global: updated.orden_global ?? task.orden_global,
                }
              : task
          ),
        }));

        console.log('✅ Tarea reubicada exitosamente');
        return { success: true, data: updated };

      } catch (error) {
        console.error('❌ Error reubicando tarea:', error);
        // Rollback a la posición previa.
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === taskId ? (prevTask || task) : task
          ),
          error: 'Error al mover la tarea: ' + error.message,
        }));
        return { success: false, error: error.message };
      }
    },

    // Reorden por LOTES tras drag & drop: un único POST y un único set()
    // optimista (antes: N update_task en paralelo = N requests + 2N renders).
    reorderTasks: async (ordenField, orders, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      if (!['orden_sprint', 'orden_global'].includes(ordenField) || !orders.length) {
        return { success: false, error: 'Parámetros de reorden no válidos' };
      }

      const prevTasks = get().tasks;
      const orderById = new Map(orders.map(o => [o.id, o.orden]));

      // Optimista: aplicar todos los órdenes en un único set.
      set(state => ({
        tasks: state.tasks.map(task =>
          orderById.has(task.id) ? { ...task, [ordenField]: orderById.get(task.id) } : task
        ),
        error: null
      }));

      try {
        const response = await apiClient.post(
          'reorder_tasks',
          { orden_field: ordenField, orders },
          authOpts(authToken)
        );
        const result = extractResponseData(response, 'reorder tasks');
        if (!result.success) {
          throw new Error(result.error || 'Error al reordenar');
        }
        return { success: true, data: result.data };
      } catch (error) {
        console.error('❌ Error reordenando tareas:', error);
        set({ tasks: prevTasks, error: 'Error al reordenar: ' + error.message });
        return { success: false, error: error.message };
      }
    },

    reopenTask: async (taskId, authToken = null) => {
      return await get().updateTask(taskId, { estado: 'pendiente' }, authToken);
    },

    // ============================================================================
    // GESTIÓN DE SPRINTS COMPLETA
    // ============================================================================
    fetchSprints: async (authToken = null, user = null) => {
      try {
        console.log('🏃 Obteniendo sprints con auth externa');
        const response = await apiClient.get('sprints', null, authOpts(authToken));
        const result = extractResponseData(response, 'sprints');
        
        if (result.success) {
          const apiSprints = Array.isArray(result.data) ? result.data : [];
          const activeSprint = apiSprints.find(sprint => sprint.estado === 'activo') || null;
          const planningSprint = apiSprints.find(sprint => sprint.estado === 'planificacion') || null;

          set({
            sprints: apiSprints,
            activeSprint: activeSprint,
            planningSprint: planningSprint,
            lastUpdate: new Date().toISOString()
          });
          
          console.log(`🏃 ${apiSprints.length} sprints obtenidos`);
          return { success: true, data: apiSprints };
        } else {
          throw new Error(result.error || 'Error al obtener sprints');
        }
        
      } catch (error) {
        console.error('❌ Error obteniendo sprints:', error);
        return { success: false, error: error.message };
      }
    },

    createSprint: async (sprintData, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('📝 Creando sprint:', sprintData);
        set({ isLoading: true, error: null });
        
        const response = await apiClient.post('create_sprint', sprintData, authOpts(authToken));
        const result = extractResponseData(response, 'sprint creation');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al crear sprint');
        }
        
        await get().fetchSprints(authToken);
        
        set({ isLoading: false });
        console.log('✅ Sprint creado exitosamente');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error creando sprint:', error);
        set({ 
          error: 'Error al crear sprint: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    // Crea un sprint en estado 'planificacion' (se llena de tareas mientras el
    // activo sigue en curso). Reutiliza createSprint inyectando estado; el
    // backend valida la invariante de "máx 1 planificación" (409 si ya hay uno).
    createPlanningSprint: async (sprintData, authToken = null) => {
      return await get().createSprint({ ...sprintData, estado: 'planificacion' }, authToken);
    },

    startSprint: async (sprintId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }

      try {
        console.log('▶️ Iniciando sprint:', sprintId);
        set({ isLoading: true, error: null });
        
        const response = await apiClient.post(`start_sprint&id=${sprintId}`, {}, authOpts(authToken));
        const result = extractResponseData(response, 'sprint start');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al iniciar sprint');
        }
        
        await get().fetchSprints(authToken);
        
        set({ isLoading: false });
        console.log('✅ Sprint iniciado exitosamente');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error iniciando sprint:', error);
        set({ 
          error: 'Error al iniciar sprint: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    // Cierra (completa) el sprint activo y reparte sus tareas SIN CERRAR
    // (validadoPorSEO != 1) según `opts.destino`:
    //   'backlog' → sprint_actual = NULL (default, compat con el flujo previo)
    //   'sprint'  → mover a opts.targetSprintId (planificación/activo del tenant)
    //   'nuevo'   → crear un sprint de planificación (opts.nuevoSprint) y mover ahí
    // Las tareas cerradas (validadoPorSEO=1) NO se mueven. El id del sprint va
    // por query (?id=); el reparto por body. Refresca sprints Y tareas porque
    // las sin-cerrar cambian de contenedor.
    closeSprint: async (sprintId, opts = {}, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }

      const { destino = 'backlog', targetSprintId = null, nuevoSprint = null } = opts || {};

      try {
        console.log('🏁 Cerrando sprint:', sprintId, { destino, targetSprintId, nuevoSprint });
        set({ isLoading: true, error: null });

        const body = { destino };
        if (destino === 'sprint' && targetSprintId) body.target_sprint_id = targetSprintId;
        if (destino === 'nuevo' && nuevoSprint) body.nuevo_sprint = nuevoSprint;

        const response = await apiClient.post(`close_sprint&id=${sprintId}`, body, authOpts(authToken));
        const result = extractResponseData(response, 'sprint close');

        if (!result.success) {
          throw new Error(result.error || 'Error al cerrar sprint');
        }

        await Promise.allSettled([
          get().fetchSprints(authToken),
          get().fetchTasks(authToken)
        ]);

        set({ isLoading: false });
        console.log('✅ Sprint cerrado exitosamente');
        return { success: true, data: result.data };

      } catch (error) {
        console.error('❌ Error cerrando sprint:', error);
        set({ 
          error: 'Error al cerrar sprint: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    deleteSprint: async (sprintId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('🗑️ Eliminando sprint:', sprintId);
        set({ isLoading: true, error: null });
        
        const response = await apiClient.del(`delete_sprint&id=${sprintId}`, authOpts(authToken));
        const result = extractResponseData(response, 'sprint deletion');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al eliminar sprint');
        }
        
        await Promise.allSettled([
          get().fetchSprints(authToken),
          get().fetchTasks(authToken)
        ]);
        
        set({ isLoading: false });
        console.log('✅ Sprint eliminado exitosamente');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error eliminando sprint:', error);
        set({ 
          error: 'Error al eliminar sprint: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    getSprintHistory: () => {
      const { sprints } = get();
      const closedSprints = sprints.filter(sprint => sprint.estado === 'completado');
      console.log(`📚 Sprint History: ${closedSprints.length} sprints completados`);
      return closedSprints;
    },

    // ============================================================================
    // GESTIÓN DE DASHBOARD Y ESTADÍSTICAS
    // ============================================================================
    fetchDashboardStats: async (authToken = null, user = null) => {
      try {
        console.log('📊 Obteniendo estadísticas con auth externa');
        const params = {};

        if (user && user.rol !== 'admin') {
          params.user_id = user.id;
        }

        const response = await apiClient.get('dashboard', params, authOpts(authToken));
        const result = extractResponseData(response, 'dashboard stats');
        
        if (result.success && result.data) {
          const apiStats = result.data;
          const mappedStats = {
            totalTasks: apiStats.total_tasks || 0,
            pendingTasks: apiStats.pending_tasks || 0,
            inProgressTasks: apiStats.in_progress_tasks || 0,
            completedTasks: apiStats.completed_tasks || 0,
            seoValidatedTasks: apiStats.seo_validated_tasks || 0,
            activeSprint: apiStats.active_sprint || null
          };
          
          set({ dashboardStats: mappedStats });
          console.log('📊 Estadísticas actualizadas');
          return { success: true, data: mappedStats };
        }
        
      } catch (error) {
        console.error('❌ Error obteniendo estadísticas:', error);
        return { success: false, error: error.message };
      }
    },

    // ============================================================================
    // GESTIÓN DE DOCUMENTOS
    // ============================================================================
    fetchDocuments: async (authToken = null, user = null) => {
      try {
        console.log('📄 Obteniendo documentos con auth externa');
        const response = await apiClient.get('documentos', null, authOpts(authToken));
        const result = extractResponseData(response, 'documents');
        
        if (result.success) {
          const apiDocs = Array.isArray(result.data) ? result.data : [];
          set({ 
            documents: apiDocs,
            lastUpdate: new Date().toISOString()
          });
          
          console.log(`📄 ${apiDocs.length} documentos obtenidos`);
          return { success: true, data: apiDocs };
        }

        return { success: false, error: result.error || 'No se pudieron cargar los documentos' };
      } catch (error) {
        console.error('❌ Error obteniendo documentos:', error);
        return { success: false, error: error.message };
      }
    },

    createDocument: async (documentData, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('📝 Creando documento:', documentData);
        set({ isLoading: true });
        
        const result = await documentationService.createDocument(documentData);
        
        set(state => ({
          documents: [...state.documents, result],
          isLoading: false
        }));
        
        console.log('✅ Documento creado exitosamente:', result);
        return { success: true, data: result };
        
      } catch (error) {
        console.error('❌ Error creando documento:', error);
        set({ isLoading: false });
        return { success: false, error: error.message };
      }
    },

    updateDocument: async (documentId, updates, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('✏️ Actualizando documento:', documentId, updates);
        set({ isLoading: true });
        
        const updatedDocument = await documentationService.updateDocument(documentId, updates);
        
        set(state => ({
          documents: state.documents.map(doc => 
            doc.id === documentId ? updatedDocument : doc
          ),
          isLoading: false
        }));
        
        console.log('✅ Documento actualizado exitosamente');
        return { success: true, data: updatedDocument };
        
      } catch (error) {
        console.error('❌ Error actualizando documento:', error);
        set({ isLoading: false });
        return { success: false, error: error.message };
      }
    },

    deleteDocument: async (documentId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('🗑️ Eliminando documento:', documentId);
        set({ isLoading: true });
        
        await documentationService.deleteDocument(documentId);
        
        set(state => ({
          documents: state.documents.filter(doc => doc.id !== documentId),
          isLoading: false
        }));
        
        console.log('✅ Documento eliminado exitosamente');
        return { success: true };
        
      } catch (error) {
        console.error('❌ Error eliminando documento:', error);
        set({ isLoading: false });
        return { success: false, error: error.message };
      }
    },

    // Sincronización de estado SIN llamada a API: las usa la pantalla de
    // documentación para reflejar al instante en el dashboard (PinnedDocs lee
    // `documents` del store) los cambios de favorito / alta / baja. NO tocan
    // isLoading (así no aparece el overlay global en la pantalla de docs).
    upsertDocument: (doc) => set(state => {
      const exists = state.documents.some(d => d.id === doc.id);
      return {
        documents: exists
          ? state.documents.map(d => (d.id === doc.id ? doc : d))
          : [doc, ...state.documents]
      };
    }),

    removeDocument: (id) => set(state => ({
      documents: state.documents.filter(d => d.id !== id)
    })),

    // ============================================================================
    // GESTIÓN DE VINCULACIONES DOCUMENTO-TAREA
    // ============================================================================
    linkDocumentToTask: async (documentId, taskId, userId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('🔗 Vinculando documento a tarea:', { documentId, taskId, userId });
        
        const result = await documentTaskService.linkDocumentToTask(documentId, taskId, userId);
        
        console.log('✅ Documento vinculado a tarea exitosamente');
        return { success: true, data: result };
        
      } catch (error) {
        console.error('❌ Error vinculando documento:', error);
        return { success: false, error: error.message };
      }
    },

    unlinkDocumentFromTask: async (documentId, taskId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('🔗 Desvinculando documento de tarea:', { documentId, taskId });
        
        const result = await documentTaskService.unlinkDocumentFromTask(documentId, taskId);
        
        console.log('✅ Documento desvinculado exitosamente');
        return { success: true, data: result };
        
      } catch (error) {
        console.error('❌ Error desvinculando documento:', error);
        return { success: false, error: error.message };
      }
    },

    getDocumentsByTask: async (taskId, authToken = null) => {
      try {
        console.log('📄 Obteniendo documentos para tarea:', taskId);
        
        const documents = await documentTaskService.getDocumentsByTask(taskId);
        
        console.log('📄 Documentos de tarea obtenidos:', documents.length);
        return documents;
        
      } catch (error) {
        console.error('❌ Error obteniendo documentos de tarea:', error);
        return [];
      }
    },

    getTasksByDocument: async (documentId, authToken = null) => {
      try {
        console.log('📋 Obteniendo tareas para documento:', documentId);
        
        const tasks = await documentTaskService.getTasksByDocument(documentId);
        
        console.log('📋 Tareas del documento obtenidas:', tasks.length);
        return tasks;
        
      } catch (error) {
        console.error('❌ Error obteniendo tareas del documento:', error);
        return [];
      }
    },

    // ============================================================================
    // GESTIÓN DE ÉPICAS CENTRALIZADAS
    // ============================================================================
    fetchEpics: async (authToken = null, includeInactive = false) => {
      try {
        console.log('🎯 Obteniendo épicas');

        const params = includeInactive ? { include_inactive: true } : null;

        const response = await apiClient.get('epics', params, authOpts(authToken));
        const result = extractResponseData(response, 'epics');

        if (result.success) {
          const apiEpics = Array.isArray(result.data) ? result.data : [];
          set({
            epics: apiEpics,
            lastUpdate: new Date().toISOString()
          });

          console.log(`🎯 ${apiEpics.length} épicas obtenidas`);
          return { success: true, data: apiEpics };
        }

        throw new Error(result.error || 'Error al obtener épicas');

      } catch (error) {
        console.error('❌ Error obteniendo épicas:', error);
        return { success: false, error: error.message };
      }
    },

    createEpic: async (epicData, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }

      try {
        console.log('📝 Creando épica:', epicData);
        set({ isLoading: true, error: null });

        const response = await apiClient.post('create_epic', epicData, authOpts(authToken));
        const result = extractResponseData(response, 'epic creation');

        if (!result.success) {
          throw new Error(result.error || 'Error al crear épica');
        }

        // Refrescar épicas
        await get().fetchEpics(authToken);

        set({ isLoading: false });
        console.log('✅ Épica creada exitosamente');
        return { success: true, data: result.data };

      } catch (error) {
        console.error('❌ Error creando épica:', error);
        set({
          error: 'Error al crear épica: ' + error.message,
          isLoading: false
        });
        return { success: false, error: error.message };
      }
    },

    updateEpic: async (epicId, updates, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }

      try {
        console.log('✏️ Actualizando épica:', epicId, updates);
        set({ isLoading: true, error: null });

        const response = await apiClient.post(`update_epic&id=${epicId}`, updates, authOpts(authToken));
        const result = extractResponseData(response, 'epic update');

        if (!result.success) {
          throw new Error(result.error || 'Error al actualizar épica');
        }

        // Actualizar estado local
        set(state => ({
          epics: state.epics.map(epic =>
            epic.id === epicId ? { ...epic, ...updates } : epic
          ),
          isLoading: false
        }));

        console.log('✅ Épica actualizada exitosamente');
        return { success: true, data: result.data };

      } catch (error) {
        console.error('❌ Error actualizando épica:', error);
        set({
          error: 'Error al actualizar épica: ' + error.message,
          isLoading: false
        });
        return { success: false, error: error.message };
      }
    },

    deleteEpic: async (epicId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }

      try {
        console.log('🗑️ Eliminando épica:', epicId);
        set({ isLoading: true, error: null });

        const response = await apiClient.post(`delete_epic&id=${epicId}`, {}, authOpts(authToken));
        const result = extractResponseData(response, 'epic deletion');

        if (!result.success) {
          throw new Error(result.error || 'Error al eliminar épica');
        }

        // Actualizar estado local
        if (result.data.deleted) {
          // Eliminar completamente
          set(state => ({
            epics: state.epics.filter(epic => epic.id !== epicId),
            isLoading: false
          }));
        } else {
          // Solo desactivar
          set(state => ({
            epics: state.epics.map(epic =>
              epic.id === epicId ? { ...epic, activo: false } : epic
            ),
            isLoading: false
          }));
        }

        console.log('✅ Épica eliminada/desactivada exitosamente');
        return { success: true, data: result.data };

      } catch (error) {
        console.error('❌ Error eliminando épica:', error);
        set({
          error: 'Error al eliminar épica: ' + error.message,
          isLoading: false
        });
        return { success: false, error: error.message };
      }
    },

    getActiveEpics: () => {
      const { epics } = get();
      return epics.filter(epic => epic.activo);
    },

    // ============================================================================
    // FUNCIONES DE REPETICIONES/CONTADORES
    // ============================================================================
    getCounterStats: async (taskId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('📊 Obteniendo estadísticas de contador:', taskId);
        
        const response = await apiClient.get('counter_stats', { id: taskId }, authOpts(authToken));
        const result = extractResponseData(response, 'counter stats');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al obtener estadísticas');
        }
        
        console.log('📊 Estadísticas de contador obtenidas');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error obteniendo estadísticas de contador:', error);
        return { success: false, error: error.message };
      }
    },

    incrementCounter: async (taskId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('➕ Incrementando contador de tarea:', taskId);
        set({ isLoading: true, error: null });
        
        const response = await apiClient.post(`increment_counter&id=${taskId}`, {}, authOpts(authToken));
        const result = extractResponseData(response, 'increment counter');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al incrementar contador');
        }
        
        // Actualizar estado local
        set(state => ({
          tasks: state.tasks.map(task => 
            task.id === taskId 
              ? { 
                  ...task, 
                  vecesCompletada: result.data.veces_completada,
                  vecesRealizada: result.data.veces_completada
                }
              : task
          ),
          currentTask: state.currentTask && state.currentTask.id === taskId
            ? {
                ...state.currentTask,
                vecesCompletada: result.data.veces_completada,
                vecesRealizada: result.data.veces_completada
              }
            : state.currentTask,
          isLoading: false
        }));
        
        console.log('✅ Contador incrementado exitosamente');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error incrementando contador:', error);
        set({ 
          error: 'Error al incrementar contador: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    resetCounter: async (taskId, authToken = null) => {
      if (!authToken) {
        return { success: false, error: 'Usuario no autenticado' };
      }
      
      try {
        console.log('🔄 Reseteando contador de tarea:', taskId);
        set({ isLoading: true, error: null });
        
        const response = await apiClient.post(`reset_counter&id=${taskId}`, {}, authOpts(authToken));
        const result = extractResponseData(response, 'reset counter');
        
        if (!result.success) {
          throw new Error(result.error || 'Error al resetear contador');
        }
        
        // Actualizar estado local
        set(state => ({
          tasks: state.tasks.map(task => 
            task.id === taskId 
              ? { ...task, vecesCompletada: 0, vecesRealizada: 0 }
              : task
          ),
          currentTask: state.currentTask && state.currentTask.id === taskId
            ? { ...state.currentTask, vecesCompletada: 0, vecesRealizada: 0 }
            : state.currentTask,
          isLoading: false
        }));
        
        console.log('✅ Contador reseteado exitosamente');
        return { success: true, data: result.data };
        
      } catch (error) {
        console.error('❌ Error reseteando contador:', error);
        set({ 
          error: 'Error al resetear contador: ' + error.message,
          isLoading: false 
        });
        return { success: false, error: error.message };
      }
    },

    // ============================================================================
    // FUNCIONES DE UTILIDAD Y CONTROL
    // ============================================================================
    clearError: () => set({ error: null }),
    setCurrentTask: (task) => set({ currentTask: task }),
    setViewMode: (mode) => set({ viewMode: mode }),
    setFilters: (filters) => set({ filters: { ...get().filters, ...filters } }),
    setSearchTerm: (term) => set({ searchTerm: term }),
    setSprintStatusFilter: (filter) => set({ sprintStatusFilter: filter }),

    // Función de refresh que requiere auth externa
    refresh: async (authToken = null, user = null) => {
      if (!authToken || !user) {
        console.warn('⚠️ refresh requiere autenticación');
        return;
      }
      
      console.log('🔄 Refrescando todos los datos...');
      
      try {
        await Promise.allSettled([
          get().fetchTasks(authToken, user),
          get().fetchSprints(authToken, user),
          get().fetchDashboardStats(authToken, user),
          get().fetchDocuments(authToken, user),
          get().fetchEpics(authToken) // 🆕 REFRESCAR ÉPICAS
        ]);
        console.log('✅ Todos los datos refrescados');
      } catch (error) {
        console.error('❌ Error refrescando datos:', error);
      }
    },
    
    // Reset del store
    reset: () => set({ ...initialState, authSyncStatus: 'disconnected' }),

    // Status de sincronización
    getSyncStatus: () => {
      const state = get();
      return {
        authSyncStatus: state.authSyncStatus,
        connectionStatus: state.connectionStatus,
        hasData: state.tasks.length > 0,
        lastSync: state.lastSync,
        lastUpdate: state.lastUpdate
      };
    }
  };
});

export default useTaskStore;